// A whole store as one download: every file under a directory, packed into a
// ZIP (stored, uncompressed: no library, readable by every unzip tool) or,
// as a fallback, a JSON document. Pure apart from reading the store.
import { joinPath, toBytes, type Storage } from './index.ts';

export interface ArchiveFile {
  path: string;
  data: Uint8Array;
}

/** Every file under `dir`, depth first, sorted by path. `onFile` reports progress. */
export async function collectFiles(storage: Storage, dir = '', onFile?: (count: number) => void): Promise<ArchiveFile[]> {
  const out: ArchiveFile[] = [];
  const walk = async (at: string): Promise<void> => {
    for (const entry of await storage.list(at)) {
      const path = joinPath(at, entry.name);
      if (entry.kind === 'dir') {
        await walk(path);
        continue;
      }
      const data = await storage.read(path);
      if (data === undefined) continue;
      out.push({ path, data: toBytes(data) });
      onFile?.(out.length);
    }
  };
  await walk(dir);
  return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** The CRC-32 a ZIP entry carries. */
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS date and time, local, as ZIP stores them. */
function dosStamp(date: Date): { time: number; day: number } {
  const year = Math.max(1980, date.getFullYear());
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    day: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

const encoder = new TextEncoder();

/**
 * Packs files into a ZIP archive with every entry stored (method 0) and
 * UTF-8 names (flag bit 11). Fails above the classic format's limits
 * (65 535 entries, 4 GiB), which a browser store does not reach.
 */
export function zipFiles(files: ArchiveFile[], date = new Date()): Uint8Array<ArrayBuffer> {
  if (files.length > 0xffff) throw new Error(`too many files for a ZIP archive: ${files.length}`);
  const { time, day } = dosStamp(date);
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.path);
    const crc = crc32(file.data);
    const size = file.data.length;
    if (offset + 30 + name.length + size > 0xffffffff) throw new Error('archive larger than 4 GiB');

    const local = new Uint8Array(30 + name.length);
    const l = new DataView(local.buffer);
    l.setUint32(0, 0x04034b50, true); // local file header
    l.setUint16(4, 20, true); // version needed
    l.setUint16(6, 0x0800, true); // UTF-8 names
    l.setUint16(8, 0, true); // stored
    l.setUint16(10, time, true);
    l.setUint16(12, day, true);
    l.setUint32(14, crc, true);
    l.setUint32(18, size, true);
    l.setUint32(22, size, true);
    l.setUint16(26, name.length, true);
    l.setUint16(28, 0, true);
    local.set(name, 30);

    const central = new Uint8Array(46 + name.length);
    const c = new DataView(central.buffer);
    c.setUint32(0, 0x02014b50, true); // central directory header
    c.setUint16(4, 20, true); // made by
    c.setUint16(6, 20, true); // version needed
    c.setUint16(8, 0x0800, true);
    c.setUint16(10, 0, true);
    c.setUint16(12, time, true);
    c.setUint16(14, day, true);
    c.setUint32(16, crc, true);
    c.setUint32(20, size, true);
    c.setUint32(24, size, true);
    c.setUint16(28, name.length, true);
    c.setUint32(42, offset, true);
    central.set(name, 46);

    locals.push(local, file.data);
    centrals.push(central);
    offset += local.length + size;
  }
  const centralSize = centrals.reduce((n, b) => n + b.length, 0);
  const end = new Uint8Array(22);
  const e = new DataView(end.buffer);
  e.setUint32(0, 0x06054b50, true); // end of central directory
  e.setUint16(8, files.length, true);
  e.setUint16(10, files.length, true);
  e.setUint32(12, centralSize, true);
  e.setUint32(16, offset, true);

  const out = new Uint8Array(offset + centralSize + end.length);
  let at = 0;
  for (const part of [...locals, ...centrals, end]) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

const decoder = new TextDecoder('utf-8', { fatal: true });

function base64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * The same files as one JSON document: text files as text, anything that is
 * not valid UTF-8 as base64. The fallback when a ZIP cannot be made.
 */
export function jsonFiles(files: ArchiveFile[], date = new Date()): string {
  return JSON.stringify(
    {
      exported: date.toISOString(),
      files: files.map((f) => {
        try {
          return { path: f.path, text: decoder.decode(f.data) };
        } catch {
          return { path: f.path, base64: base64(f.data) };
        }
      }),
    },
    null,
    2,
  );
}
